import { ErrorMessage } from "@hookform/error-message";
import { FormRow } from "components/ui/form";
import Label from "components/ui/form/Label";
import Row from "components/ui/layout/Row";
import type { Course, Hole } from "Models";
import { Controller, useFormContext } from "react-hook-form";
import Select, { type SingleValue } from "react-select";
import { MultiSelect } from "react-multi-select-component";
import { ArrayHelper } from "utils/arrays/arrayHelpers";
import { customStyles } from "utils/ui/uiHelpers";

type CourseAndHolesFormProps = {
  courses: Course[];
  holes: Hole[];
};

const CourseAndHolesForm: React.FC<CourseAndHolesFormProps> = ({
  courses,
  holes,
}) => {
  const { control, watch, formState, setValue } = useFormContext();
  const watchAll = watch();

  const courseOptions: { label: string; value: Course }[] = courses.map(
    (course) => {
      return { label: course.displayName || course.shortName, value: course };
    },
  );

  const handleMultiselectFieldChange = (
    e: { label: string; value: any }[],
    field: string,
  ) => {
    setValue(
      field,
      e.map((x) => x.value),
    );
  };

  const handleCourseChange = (
    e: SingleValue<{ label: string; value: Course }>,
  ) => {
    if (!e || !e.value || !e.value.shortName) {
      return;
    }
    setValue("roundInfo.courseName", e.value.shortName);
  };

  const selectFront9 = () => setValue("holes", ArrayHelper.fillWithRange(1, 9));

  const selectBack9 = () =>
    setValue("holes", ArrayHelper.fillWithRange(10, 18));

  const selectAll18 = () => setValue("holes", ArrayHelper.fillWithRange(1, 18));

  return (
    <div>
      <FormRow header="Which course are you playing?">
        <Controller
          name="roundInfo.courseName"
          control={control}
          rules={{ required: "Course selection is required" }}
          render={({ field }) => (
            <Select
              closeMenuOnSelect={true}
              isMulti={false}
              {...field}
              options={courseOptions}
              value={courseOptions.find(
                (option) => option.value.shortName === field.value,
              )}
              onChange={handleCourseChange}
              styles={customStyles}
            />
          )}
        />
        <ErrorMessage
          errors={formState.errors}
          name="roundInfo.courseName"
          render={({ message }) => (
            <div className="text-sm text-red-500">{message}</div>
          )}
        />
      </FormRow>
      {watchAll.roundInfo?.courseName ? (
        <Row cssClasses="pt-4">
          <Label
            name="Which holes do you want to play?"
            cssClasses="text-xs font-normal text-gray-800 dark:text-gray-200 poppins pb-3 mt-5"
          />
          {holes?.length === 18 && (
            <div className="flex flex-row mb-5">
              <div
                onClick={selectFront9}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && selectFront9()
                }
                role="button"
                tabIndex={0}
                className="border rounded-full bg-brandGreen hover:bg-brandYellow text-sm text-white hover:text-black font-bold px-4 py-2 mt-2 inline items-center hover:cursor-pointer w-1/3 text-center mr-1"
              >
                Select Front 9
              </div>
              <div
                onClick={selectBack9}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && selectBack9()
                }
                role="button"
                tabIndex={0}
                className="border rounded-full bg-brandGreen hover:bg-brandYellow text-sm text-white hover:text-black font-bold px-4 py-2 mt-2 inline items-center hover:cursor-pointer w-1/3 text-center mr-1"
              >
                Select Back 9
              </div>
              <div
                onClick={selectAll18}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && selectAll18()
                }
                role="button"
                tabIndex={0}
                className="border rounded-full bg-brandGreen hover:bg-brandYellow text-sm text-white hover:text-black font-bold px-4 py-2 mt-2 inline items-center hover:cursor-pointer w-1/3 text-center"
              >
                Select All 18
              </div>
            </div>
          )}
          <Controller
            name="holes"
            key="holes"
            control={control}
            rules={{ required: "Hole selection is required" }}
            render={({ field }) => {
              return (
                <MultiSelect
                  {...field}
                  options={holes.map((hole) => {
                    return {
                      label: `${hole.number} Par: ${hole.par}`,
                      value: hole.number,
                    };
                  })}
                  value={holes
                    .filter((hole) => watchAll.holes?.includes(hole.number))
                    .map((hole) => {
                      return {
                        label: hole.number.toString(),
                        value: hole.number,
                      };
                    })}
                  onChange={(e: { label: string; value: number }[]) =>
                    handleMultiselectFieldChange(e, "holes")
                  }
                  labelledBy="Holes"
                  hasSelectAll={true}
                  overrideStrings={{
                    selectAll: "Select/Unselect All Holes",
                  }}
                  disableSearch={true}
                  className="dark:text-black dark:placeholder-black rounded-lg text-xs"
                />
              );
            }}
          />
        </Row>
      ) : null}
      <ErrorMessage
        errors={formState.errors}
        name="holes"
        render={({ message }) => (
          <div className="text-sm text-red-500">{message}</div>
        )}
      />
    </div>
  );
};

export default CourseAndHolesForm;
